import { initCharts } from "@/lib/chart";
import { initIconify } from "@/lib/iconify";
import { initPlausible } from "@/lib/plausible";
import axios from "axios";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { AlertmousePage } from "./current";

//
// Global initialization code for app and tests
//

export async function init() {
  initCharts();
  initDayjs();
  initPlausible();
  await initIconify();
}

function initDayjs() {
  dayjs.extend(advancedFormat);
  dayjs.extend(utc);
  dayjs.extend(timezone);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initSentry(app: any, initialPage: AlertmousePage) {
  const { cargo } = initialPage.props;

  // https://docs.sentry.io/platforms/javascript/configuration/options
  Sentry.init({
    app,
    // "data source name" which is where we send events. This is not private, and is embedded in both ruby & js.
    dsn: "https://1f2dd396bcd5da488fde9e2f70e7b047@o4511298975563776.ingest.us.sentry.io/4511298992603136",
    // debug: true, // noisy, but helpful for debugging sentry callsites
    release: cargo.revision,
    sendDefaultPii: true,

    // hooks
    beforeSend(event) {
      // scrub cookies
      delete event.request?.cookies;
      return event;
    },
  });

  if (cargo.user) {
    const { id, name: username, email } = cargo.user;
    Sentry.setUser({ id, username, email });
  }
}

//
// inertia uses the cookie name XSRF-TOKEN (the axios default), but we want to
// send the header X-CSRF-TOKEN for compatibility with rails. Inertia-rails uses
// middleware to copy one to the other, but only for inertia requests. We want
// it to work for ALL requests, including js-from-routes.
//

axios.defaults.xsrfHeaderName = "X-CSRF-TOKEN";

//
// use an axios interceptor to run transformProps
// https://github.com/inertiajs/inertia/discussions/1961
//

axios.interceptors.response.use((response) => {
  if (!response.data) return response;

  try {
    const isInertia = response.headers.vary === "X-Inertia";
    let data = response.data;
    if (isInertia) {
      data = JSON.parse(data);
      data.props = Util.transformProps(data.props);
    } else if (isPlainObject(data)) {
      data = Util.transformProps(data);
    }
    response.data = data;
  } catch (error) {
    // hm. log, but let the response complete
    console.error(error);
  }
  return response;
});

if (import.meta.env.MODE === "development") {
  // hack - suppress <Suspense> warning from vite
  const originalInfo = console.info;
  console.info = (...args) => {
    if (isString(args[0]) && args[0].startsWith("<Suspense>")) return;
    originalInfo(...args);
  };
}
